import { useMemo } from "react";
import { useCSROrders } from "../services";
import { statusFilterMap } from "../../../../constants";

const initialSummary = {
  open: 0,
  inTransit: 0,
  outForDelivery: 0,
  partialDelivery: 0,
  delivered: 0,
  deliveredItalics: 0,
  deliveredStar: 0,
  deliveredStarStar: 0,
};

function SummaryItem({ open, inTransit, outForDelivery, delivered }) {
  return (
    <div className="row text-center container m-auto">
      <div className="col-md-3">Open Orders: {open}</div>
      <div className="col-md-3">In Transit Orders: {inTransit}</div>
      <div className="col-md-3">Out for Delivery Orders: {outForDelivery}</div>
      <div className="col-md-3">Delivered Orders: {delivered}</div>
    </div>
  );
}

function StatusSummary({
  searchCategory,
  fromDate,
  toDate,
  invoices,
  barcodes,
  shouldFetchAllStatuses,
  maxStatusFetchDateRange,
}) {
  const {
    data: allOrdersData,
    isLoading,
    isError,
    isFetching,
    error,
  } = useCSROrders({
    searchCategory,
    fromDate,
    toDate,
    invoices,
    barcodes,
    isClientSide: true,
    isEnabledAllOrders: shouldFetchAllStatuses,
  });

  const summary = useMemo(() => {
    if (
      !Array.isArray(allOrdersData?.deliveryList) ||
      allOrdersData?.deliveryList?.length === 0 ||
      isFetching === true
    )
      return initialSummary;

    return allOrdersData.deliveryList.reduce((acc, order) => {
      const { status } = order;

      if (statusFilterMap.open.includes(status)) {
        return { ...acc, open: acc.open + 1 };
      }

      if (statusFilterMap.inTransit.includes(status)) {
        return { ...acc, inTransit: acc.inTransit + 1 };
      }

      if (statusFilterMap.outForDelivery.includes(status)) {
        return { ...acc, outForDelivery: acc.outForDelivery + 1 };
      }

      if (statusFilterMap.partialDelivery.includes(status)) {
        return { ...acc, partialDelivery: acc.partialDelivery + 1 };
      }

      if (
        statusFilterMap.delivered.includes(status) ||
        statusFilterMap.possibleException.includes(status)
      ) {
        return { ...acc, delivered: acc.delivered + 1 };
      }

      return acc;
    }, initialSummary);
  }, [allOrdersData, isFetching]);

  if (!shouldFetchAllStatuses) {
    return (
      <div className="border-top tw-text-center text-note font-600">
        Status summary and filtering disabled for date ranges over{" "}
        {maxStatusFetchDateRange} days.
      </div>
    );
  }
  if (isLoading || isFetching) {
    return (
      <div className="border-top">
        <SummaryItem
          open="Loading..."
          inTransit="Loading..."
          outForDelivery="Loading..."
          delivered="Loading..."
        />
      </div>
    );
  }

  if (isError) {
    return (
      <>
        <div className="border-top">
          <SummaryItem
            open="Error"
            inTransit="Error"
            outForDelivery="Error"
            delivered="Error"
          />

          <div className="tw-text-red-500 tw-font-medium tw-text-center">
            There was an error loading the order summary, Error Code:
            {error?.response?.status || "Unknown"}
            {" - "}
            Please try again later.
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="border-top">
      <SummaryItem
        open={summary.open}
        inTransit={summary.inTransit}
        outForDelivery={summary.outForDelivery}
        delivered={
          summary.delivered +
          summary.deliveredItalics +
          summary.deliveredStar +
          summary.deliveredStarStar +
          summary.partialDelivery
        }
      />
    </div>
  );
}

export default StatusSummary;
